import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Globe, Boxes, CreditCard, Settings, LifeBuoy, Tag,
  Users, Mail, Webhook, RefreshCw, Send, BarChart3, MessageSquare, Server
} from "lucide-react";
import { cn } from "@/lib/utils";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { useAccount } from "@/hooks/useAccount";

/**
 * Left navigation. Renders both the desktop column AND the mobile drawer.
 *
 * Role-aware: admins get the operator navigation (the admin area is their
 * primary view); customers get the account navigation. The backend still
 * gates every admin endpoint, so this is purely UX.
 */

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  end?: boolean;
}

const CUSTOMER_NAV: NavItem[] = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/domains",   label: "Domains",  icon: Globe },
  { to: "/add-ons",   label: "Add-ons",  icon: Boxes },
  { to: "/billing",   label: "Billing",  icon: CreditCard }
];

const CUSTOMER_SECONDARY: NavItem[] = [
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/support",  label: "Support",  icon: LifeBuoy, badge: "soon" }
];

const ADMIN_NAV: NavItem[] = [
  { to: "/admin",             label: "Overview",    icon: LayoutDashboard, end: true },
  { to: "/admin/provisioning", label: "Setup queue", icon: Server },
  { to: "/admin/reports",     label: "Reports",     icon: BarChart3 },
  { to: "/admin/customers",   label: "Customers",   icon: Users },
  { to: "/admin/support",    label: "Support",    icon: MessageSquare },
  { to: "/admin/leads",      label: "Leads",      icon: Mail },
  { to: "/admin/webhooks",   label: "Webhooks",   icon: Webhook },
  { to: "/admin/plans",      label: "Plans",      icon: Tag },
  { to: "/admin/add-ons",    label: "Add-ons",    icon: Boxes },
  { to: "/admin/sync",       label: "Sync",       icon: RefreshCw },
  { to: "/admin/email",      label: "Send email", icon: Send }
];

const ADMIN_SECONDARY: NavItem[] = [
  { to: "/settings", label: "Settings", icon: Settings }
];

interface SidebarProps {
  onNavigate?: () => void;
}

export function Sidebar({ onNavigate }: SidebarProps) {
  const { data: account } = useAccount();
  const isAdmin = account?.user_role === "Admin";

  const primary   = isAdmin ? ADMIN_NAV : CUSTOMER_NAV;
  const secondary = isAdmin ? ADMIN_SECONDARY : CUSTOMER_SECONDARY;

  return (
    <nav aria-label="Primary" className="flex h-full flex-col gap-6 px-3 py-5 sm:px-4">
      {/* Logo — seated on a white chip so the brand mark reads on the dark rail */}
      <div className="px-1">
        <div className="inline-flex items-center rounded-lg bg-white px-3 py-2">
          <BrandLogo className="h-7 w-auto" />
        </div>
      </div>

      {isAdmin && (
        <div className="-mt-3 px-1">
          <span className="rounded-full bg-white/10 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-white/80">
            Operator
          </span>
        </div>
      )}

      <NavGroup items={primary} onNavigate={onNavigate} />

      <div className="mt-auto">
        <NavGroup items={secondary} onNavigate={onNavigate} />
      </div>
    </nav>
  );
}

function NavGroup({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  return (
    <ul className="flex flex-col gap-1">
      {items.map(item => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            onClick={onNavigate}
            end={item.end ?? false}
            className={({ isActive }) =>
              cn(
                "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                "min-h-[44px]",
                isActive
                  ? "bg-white text-[hsl(222,47%,11%)] shadow-sm"
                  : "text-[hsl(var(--sidebar-foreground))] hover:bg-white/[0.06] hover:text-white"
              )
            }
          >
            <item.icon className="h-4 w-4 shrink-0" />
            <span className="flex-1">{item.label}</span>
            {item.badge && (
              <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white/60">
                {item.badge}
              </span>
            )}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}
